/**
 * Single-owner turn registry (HIG-5970, design:
 * docs/research/chat-engine-design-no-effect.md §4).
 *
 * One AbortController per key, one active turn per key — starting a new turn
 * aborts and supersedes the old one, so double-subscribe (and the
 * POST+`/subscribe` double-writer race) is impossible by construction. Keys
 * are normally chat ids; a queued submission starts under a side key and
 * adopts the chat key once its stream takes over (`adoptTurnKey`), so the
 * single-owner-per-chat invariant holds for whichever stream is live. The
 * map-identity `isOwner` check replaces the Symbol ownership tokens the hook
 * used: a superseded turn's async cleanup sees `isOwner() === false` and
 * leaves the new owner's state alone.
 */
import type { TurnInterruptReason } from './errors'

export type TurnHandle<T = unknown> = {
  /** The key the turn was started under (it may later adopt another key). */
  readonly key: string
  readonly controller: AbortController
  /** Settles when the turn's run function settles; never rejects for engine runs. */
  readonly done: Promise<T>
}

export type TurnRun<T> = (signal: AbortSignal, isOwner: () => boolean) => PromiseLike<T>

const active = new Map<string, AbortController>()
/** Each live controller's current registry key — updated by `adoptTurnKey`. */
const currentKey = new Map<AbortController, string>()

/**
 * Start (or supersede) the turn for a key. The run function receives the
 * turn's abort signal and an `isOwner` check that stays true until another
 * turn supersedes this one — cleanup that mutates shared per-chat state must
 * gate on it.
 */
export function startTurn<T>(key: string, run: TurnRun<T>): TurnHandle<T> {
  active.get(key)?.abort('superseded' satisfies TurnInterruptReason)

  const controller = new AbortController()
  active.set(key, controller)
  currentKey.set(controller, key)
  const isOwner = () => {
    const k = currentKey.get(controller)
    return k !== undefined && active.get(k) === controller
  }

  // Invoked synchronously so the turn's synchronous prefix (streaming markers)
  // lands before startTurn returns — callers read them right after subscribing.
  const done = Promise.resolve(run(controller.signal, isOwner)).finally(() => {
    const k = currentKey.get(controller)
    if (k !== undefined && active.get(k) === controller) active.delete(k)
    currentKey.delete(controller)
  })

  return { key, controller, done }
}

/**
 * Migrate a live turn to a new registry key, superseding (aborting) whatever
 * currently holds it. Used when a stream becomes the chat's owner under a
 * different identity than it started with: a queued submission's held-open
 * stream taking over at `metadata`, and a new chat's temp id becoming the
 * backend id. Without this, later turns/subscribes keyed by the chat id would
 * not supersede the live stream — the exact double-writer race the registry
 * exists to prevent. No-op when the old key holds no turn.
 */
export function adoptTurnKey(oldKey: string, newKey: string): void {
  if (oldKey === newKey) return
  const controller = active.get(oldKey)
  if (!controller) return
  active.get(newKey)?.abort('superseded' satisfies TurnInterruptReason)
  active.delete(oldKey)
  active.set(newKey, controller)
  currentKey.set(controller, newKey)
}

export function isTurnRegistered(chatId: string): boolean {
  return active.has(chatId)
}

export function __resetTurnRegistryForTests(): void {
  active.clear()
  currentKey.clear()
}
